import React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
} from '@mui/material'
import { useEventDepthData } from '../layers/event_depth_data_binsearch_dictcomp'
import type { GeoJsonFeature } from '../utils/ogc'
import { useSensorLocations } from '../layers/sensor_locations'
import MiniDepthChart from '../components/charts/MiniDepthChart'
import { SensorLegendTable } from '../components/scrubber/SensorDataGrid'
import { format } from 'date-fns'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'

export const Route = createFileRoute('/download')({
  component: DownloadPage,
})

function parseInput(val: string) {
  const d = new Date(val)
  return isNaN(+d) ? null : d
}

export default function DownloadPage() {
  const [fromInput, setFromInput] = React.useState(() => new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 16))
  const [toInput, setToInput] = React.useState(() => new Date().toISOString().slice(0, 16))
  const fromDate = parseInput(fromInput) || new Date(Date.now() - 24 * 60 * 60 * 1000)
  const toDate = parseInput(toInput) || new Date()
  const timeRange: [Date, Date, boolean] = [fromDate, toDate, false]

  const { features, loading } = useEventDepthData(timeRange)
  const { features: sensors, loading: sensorsLoading } = useSensorLocations()
  const [highlightedKey, setHighlightedKey] = React.useState<string | null>(null)
  const cardSx = React.useMemo(
    () => ({
      p: 3,
      borderRadius: 3,
      background: 'background.paper',
      borderColor: 'divider',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }),
    []
  )

  const rows = React.useMemo(() => {
    if (!features) return []
    return features.map((f: GeoJsonFeature) => f.properties)
  }, [features])

  const columns = React.useMemo(() => {
    const keys = new Set<string>()
    rows.forEach((r) => Object.keys(r || {}).forEach((k) => keys.add(k)))
    return Array.from(keys)
  }, [rows])
  const depthCols = React.useMemo<GridColDef[]>(() => columns.map((c) => ({ field: c, headerName: c, flex: 1, minWidth: 120 })), [columns])
  const depthRows = React.useMemo(() => rows.map((r, i) => ({ id: i, ...r })), [rows])

  const downloadCsv = React.useCallback(() => {
    if (!rows.length) return
    const safe = (v: any) => {
      if (v == null) return ''
      const s = v instanceof Date ? v.toISOString() : String(v)
      const needsWrap = s.includes(',') || s.includes('"') || s.includes('\n')
      const escaped = s.replace(/"/g, '""')
      return needsWrap ? `"${escaped}"` : escaped
    }
    const header = columns.join(',')
    const body = rows.map((r) => columns.map((c) => safe((r as any)[c])).join(',')).join('\n')
    const csv = `${header}\n${body}`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `floodnet-depth-${fromDate.toISOString()}-${toDate.toISOString()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [rows, columns, fromDate, toDate])

  type AugmentedSensor = GeoJsonFeature & {
    properties: GeoJsonFeature['properties'] & {
      time: Date[]
      depth_inches: number[]
      max_depth_inches?: number
      current_depth_inches?: number
    }
  }

  const chartFeatures = React.useMemo<AugmentedSensor[]>(() => {
    if (!sensors) return []
    const byId: Record<string, AugmentedSensor> = {}
    sensors.forEach((s) => {
      const id = (s.properties as any)?.deployment_id || (s.properties as any)?.id || s.id || ''
      byId[id] = {
        ...s,
        properties: {
          ...s.properties,
          time: [],
          depth_inches: [],
          max_depth_inches: 0,
        },
      }
    })
    features?.forEach((f) => {
      const id = f.properties.deployment_id
      const target = byId[id]
      if (!target) return
      target.properties.time.push(f.properties.time)
      target.properties.depth_inches.push(f.properties.depth_inches)
      target.properties.max_depth_inches = Math.max(target.properties.max_depth_inches || 0, f.properties.depth_inches)
    })
    return Object.values(byId).filter((f) => f.properties.time.length)
  }, [sensors, features])

  const handleFeatureClick = React.useCallback(
    (f: GeoJsonFeature) => {
      const id = (f.properties as any)?.deployment_id ?? (f.properties as any)?.id ?? f.id ?? ''
      setHighlightedKey((prev) => (prev === id ? null : id))
    },
    []
  )

  const sensorRows = React.useMemo(() => sensors?.map((s) => s.properties) || [], [sensors])
  const sensorColumns = React.useMemo(() => {
    const keys = new Set<string>()
    sensorRows.forEach((r) => Object.keys(r || {}).forEach((k) => keys.add(k)))
    return Array.from(keys)
  }, [sensorRows])
  const sensorCols = React.useMemo<GridColDef[]>(() => sensorColumns.map((c) => ({ field: c, headerName: c, flex: 1, minWidth: 120 })), [sensorColumns])
  const sensorGridRows = React.useMemo(() => sensorRows.map((r, i) => ({ id: i, ...r })), [sensorRows])
  const downloadSensorCsv = React.useCallback(() => {
    if (!sensorRows.length) return
    const safe = (v: any) => {
      if (v == null) return ''
      const s = v instanceof Date ? v.toISOString() : String(v)
      const needsWrap = s.includes(',') || s.includes('"') || s.includes('\n')
      const escaped = s.replace(/"/g, '""')
      return needsWrap ? `"${escaped}"` : escaped
    }
    const header = sensorColumns.join(',')
    const body = sensorRows.map((r) => sensorColumns.map((c) => safe((r as any)[c])).join(',')).join('\n')
    const csv = `${header}\n${body}`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `floodnet-sensors-${new Date().toISOString()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [sensorRows, sensorColumns])

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 900 }}>
            Data Download
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Preview and export depth measurements and sensor metadata for any time window. All available properties are included in the CSVs.
          </Typography>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 3,
            background: 'background.paper',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Sensor Preview
          </Typography>
          <MiniDepthChart
            timeRange={timeRange}
            currentTime={undefined}
            features={chartFeatures}
            precip={[]}
            highlightedKey={highlightedKey}
            onClick={handleFeatureClick}
            height={220}
            loading={loading || sensorsLoading}
            caption={chartFeatures.length ? `${chartFeatures.length} sensors with data` : undefined}
          />
          <SensorLegendTable
            features={chartFeatures as any}
            currentTime={undefined}
            highlightedKey={highlightedKey}
            onSelect={handleFeatureClick}
          />
        </Paper>

        <Paper
          variant="outlined"
          sx={cardSx}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                CSV Exports
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Depth measurements and sensor metadata.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                disabled={!rows.length || loading}
                onClick={downloadCsv}
              >
                Depth CSV
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={!sensorRows.length || sensorsLoading}
                onClick={downloadSensorCsv}
              >
                Sensors CSV
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="From"
              type="datetime-local"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="To"
              type="datetime-local"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          <Paper variant="outlined" sx={{ height: 320 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, py: 1 }}>
              Depth preview ({rows.length} rows)
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <DataGrid
                rows={depthRows}
                columns={depthCols}
                density="compact"
                hideFooter
                disableColumnMenu
                disableColumnSelector
                disableColumnFilter
                disableRowSelectionOnClick
              />
            )}
          </Paper>

          <Paper variant="outlined" sx={{ height: 320 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, py: 1 }}>
              Sensor preview ({sensorRows.length} rows)
            </Typography>
            {sensorsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <DataGrid
                rows={sensorGridRows}
                columns={sensorCols}
                density="compact"
                hideFooter
                disableColumnMenu
                disableColumnSelector
                disableColumnFilter
                disableRowSelectionOnClick
              />
            )}
          </Paper>
        </Paper>
      </Box>
    </Box>
  )
}
